/*
 * Copyright Higharc Inc. 2019
 */

/* These utilities give out values for a tetrahedron where one face is facing directly down, with one edge of that face
   being parallel to the x axis. Here's a top-down view of the tetrahedron with the top-facing faces numbered.
   The bottom face is numbered 0.

      .
     /|\
    / | \
   /3 | 2\     -z
  /  / \  \    ^
 / /  1  \ \   |
-------------  *--> x

*/
import {
  ArrowHelper,
  BufferAttribute,
  BufferGeometry,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  Vector3,
} from "three";

const tempQuaternion = new Quaternion();

export const TetrahedronUtil = {
  faceNormal: function (faceIndex) {
    switch (faceIndex) {
      case 0:
        return new Vector3(0, -1, 0);
      default:
        // Reference: https://en.wikipedia.org/wiki/Tetrahedron
        const normal = new Vector3(0, 1 / 3, Math.sqrt(8) / 3);
        if (faceIndex > 1) {
          tempQuaternion.setFromAxisAngle(new Vector3(0, 1, 0), ((Math.PI * 2) / 3) * (faceIndex - 1));
          normal.applyQuaternion(tempQuaternion);
        }
        return normal;
    }
  },

  // Note that we could also reuse the built-in TetrahedronGeometry, but we want the geometry in a specific
  // orientation, so we have our own helper for creating it.
  createGeometry: function (edgeLength) {
    const geometry = new BufferGeometry();
    const bottomY = -edgeLength / Math.sqrt(24); // From radius of insphere.
    const topY = edgeLength * Math.sqrt(3 / 8); // From radius of circumsphere.
    const vertices = new Float32Array([
      // face 0 (bottom, clockwise from a top-down perspective):
      0.0,
      bottomY,
      -edgeLength / Math.sqrt(3),
      edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),
      -edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),

      // face 1 (facing towards positive z, CCW from a top-down perspective):
      0.0,
      topY,
      0.0,
      -edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),
      edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),

      // face 2 (facing towards positive x, CCW from a top-down perspective):
      0.0,
      topY,
      0.0,
      edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),
      0.0,
      bottomY,
      -edgeLength / Math.sqrt(3),

      // face 3 (facing towards negative x, CCW from a top-down perspective):
      0.0,
      topY,
      0.0,
      0.0,
      bottomY,
      -edgeLength / Math.sqrt(3),
      -edgeLength / 2,
      bottomY,
      edgeLength / (2 * Math.sqrt(3)),
    ]);
    geometry.setAttribute("position", new BufferAttribute(vertices, 3));
    return geometry;
  },

  createFaceNormalsHelper: function () {
    const parentObj = new Object3D();
    const colors = [0xffffff, 0xff0000, 0x00ff00, 0x0000ff];
    for (let faceIndex = 0; faceIndex < 4; ++faceIndex) {
      const normal = TetrahedronUtil.faceNormal(faceIndex);
      const normalHelper = new ArrowHelper(normal, new Vector3(0, 0, 0), 1, colors[faceIndex]);
      parentObj.add(normalHelper);
    }

    const tetraGeometry = TetrahedronUtil.createGeometry(0.2);
    const faceMaterial = new MeshBasicMaterial({ color: 0x888888 });
    faceMaterial.transparent = true;
    faceMaterial.opacity = 0.5;
    parentObj.add(new Mesh(tetraGeometry, faceMaterial));

    return parentObj;
  },
};
